import React from 'react';
import { TrendingUp, Award, Zap, Clock, Star, CheckCircle, AlertCircle } from 'lucide-react';

const ProfileStrengthWidget = ({ profile }) => {
  // Calculate profile completeness
  const calculateCompleteness = () => {
    if (!profile) return 0;
    
    let completed = 0;
    const total = 10;
    
    if (profile.company_name) completed++;
    if (profile.short_description && profile.short_description.length >= 50) completed++;
    if (profile.long_description && profile.long_description.length >= 200) completed++;
    if (profile.categories && profile.categories.length >= 1) completed++;  // Changed from 2 to 1
    if (profile.location_city && profile.location_zip) completed++;
    if (profile.price_range) completed++;
    if (profile.images && profile.images.length >= 3) completed++;
    if (profile.website_link || profile.instagram_link) completed++;
    if (profile.email) completed++;  // Changed from contact_email to email
    if (profile.profil_status === 'aktiv') completed++;
    
    return Math.round((completed / total) * 100);
  };

  const completeness = calculateCompleteness();

  // Determine level and benefits
  const getLevel = (score) => {
    if (score >= 90) return { name: 'Platin', color: 'from-gray-400 to-gray-600', icon: '💎', next: null };
    if (score >= 75) return { name: 'Gold', color: 'from-yellow-400 to-yellow-600', icon: '🏆', next: 'Platin' };
    if (score >= 50) return { name: 'Silber', color: 'from-gray-300 to-gray-500', icon: '🥈', next: 'Gold' };
    return { name: 'Bronze', color: 'from-orange-400 to-orange-600', icon: '🥉', next: 'Silber' };
  };

  const level = getLevel(completeness);

  // Calculate badges earned
  const badges = [];
  
  if (profile?.images && profile.images.length >= 5) {
    badges.push({ name: 'Portfolio-Profi', icon: '📸', color: 'bg-purple-100 text-purple-800' });
  }
  if (profile?.long_description && profile.long_description.length >= 500) {
    badges.push({ name: 'Storyteller', icon: '📖', color: 'bg-[#FFF0D1] text-[#2B2142]' });
  }
  if (profile?.categories && profile.categories.length >= 5) {
    badges.push({ name: 'Vielseitig', icon: '🎯', color: 'bg-green-100 text-green-800' });
  }
  if (profile?.website_link && profile?.instagram_link) {
    badges.push({ name: 'Vernetzt', icon: '🌐', color: 'bg-indigo-100 text-indigo-800' });
  }

  // Tips for improvement
  const getTips = () => {
    const tips = [];
    
    if (!profile?.images || profile.images.length < 3) {
      tips.push('Füge mindestens 3 Portfolio-Bilder hinzu (+20% Anfragen)');
    }
    if (!profile?.long_description || profile.long_description.length < 200) {
      tips.push('Schreibe eine ausführliche Beschreibung (+15% Vertrauen)');
    }
    if (!profile?.categories || profile.categories.length < 1) {
      tips.push('Wähle mindestens eine Kategorie für bessere Sichtbarkeit');
    }
    if (!profile?.website_link && !profile?.instagram_link) {
      tips.push('Verlinke deine Social Media Profile (+10% Glaubwürdigkeit)');
    }
    
    return tips;
  };

  const tips = getTips();

  return (
    <div className="bg-white rounded-none sm:rounded-3xl shadow-xl p-4 sm:p-6 border-0 sm:border-2 border-gray-100">
      <div className="flex items-center justify-between mb-4 sm:mb-6">
        <div className="flex items-center gap-2 sm:gap-3">
          <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-gradient-to-br ${level.color} flex items-center justify-center text-xl sm:text-2xl shadow-lg`}>
            {level.icon}
          </div>
          <div>
            <h3 className="text-base sm:text-xl font-bold text-gray-900">Profilstärke</h3>
            <p className="text-xs sm:text-sm text-gray-600">Level: {level.name}</p>
          </div>
        </div>
        <div className="text-right">
          <div className="text-2xl sm:text-3xl font-bold text-gray-900">{completeness}%</div>
          <p className="text-xs text-gray-600">Vollständig</p>
        </div>
      </div>

      {/* Progress Bar */}
      <div className="mb-4 sm:mb-6">
        <div className="h-3 bg-gray-100 rounded-full overflow-hidden">
          <div
            className={`h-full bg-gradient-to-r ${level.color} transition-all duration-500 rounded-full`}
            style={{ width: `${completeness}%` }}
          />
        </div>
        {level.next && (
          <p className="text-xs text-gray-600 mt-2 text-right">
            Noch {90 - completeness}% bis {level.next}
          </p>
        )}
      </div>

      {/* Badges */}
      {badges.length > 0 && (
        <div className="mb-6">
          <h4 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
            <Award className="w-4 h-4" />
            Deine Badges ({badges.length})
          </h4>
          <div className="flex flex-wrap gap-2">
            {badges.map((badge, idx) => (
              <div
                key={idx}
                className={`px-3 py-1.5 ${badge.color} rounded-full text-xs font-semibold flex items-center gap-1.5`}
              >
                <span>{badge.icon}</span>
                <span>{badge.name}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Quality Indicators */}
      <div className="mb-6 grid grid-cols-2 gap-3">
        <div className="flex items-center gap-2 text-sm">
          {profile?.images && profile.images.length >= 3 ? (
            <CheckCircle className="w-4 h-4 text-green-600" />
          ) : (
            <AlertCircle className="w-4 h-4 text-orange-500" />
          )}
          <span className="text-gray-700">Portfolio-Bilder</span>
        </div>
        <div className="flex items-center gap-2 text-sm">
          {profile?.long_description && profile.long_description.length >= 200 ? (
            <CheckCircle className="w-4 h-4 text-green-600" />
          ) : (
            <AlertCircle className="w-4 h-4 text-orange-500" />
          )}
          <span className="text-gray-700">Beschreibung</span>
        </div>
        <div className="flex items-center gap-2 text-sm">
          {profile?.categories && profile.categories.length >= 1 ? (
            <CheckCircle className="w-4 h-4 text-green-600" />
          ) : (
            <AlertCircle className="w-4 h-4 text-orange-500" />
          )}
          <span className="text-gray-700">Kategorien</span>
        </div>
        <div className="flex items-center gap-2 text-sm">
          {profile?.website_link || profile?.instagram_link ? (
            <CheckCircle className="w-4 h-4 text-green-600" />
          ) : (
            <AlertCircle className="w-4 h-4 text-orange-500" />
          )}
          <span className="text-gray-700">Social Links</span>
        </div>
      </div>

      {/* Tips */}
      {tips.length > 0 && (
        <div className="bg-gradient-to-br from-[#FFF0D1]/30 to-[#F4F2F2] rounded-xl p-4 border border-[#2B2142]/20">
          <h4 className="text-sm font-semibold text-gray-900 mb-2 flex items-center gap-2">
            <Zap className="w-4 h-4 text-yellow-500" />
            Verbesserungsvorschläge
          </h4>
          <ul className="space-y-2">
            {tips.slice(0, 2).map((tip, idx) => (
              <li key={idx} className="text-xs text-gray-700 flex items-start gap-2">
                <TrendingUp className="w-3 h-3 mt-0.5 text-[#2B2142] flex-shrink-0" />
                <span>{tip}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Status Summary */}
      <div className="mt-6 pt-6 border-t border-gray-200">
        <div className="flex items-center justify-between text-sm">
          <span className="text-gray-600">Profilsichtbarkeit</span>
          <span className={`font-semibold ${
            completeness >= 75 ? 'text-green-600' : completeness >= 50 ? 'text-yellow-600' : 'text-red-600'
          }`}>
            {completeness >= 75 ? 'Hoch' : completeness >= 50 ? 'Mittel' : 'Niedrig'}
          </span>
        </div>
      </div>
    </div>
  );
};

export default ProfileStrengthWidget;
